import React, { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import MuiDirectionsIcon from '@mui/icons-material/Directions';
import MuiLocationOnIcon from '@mui/icons-material/LocationOn';
import MuiEventIcon from '@mui/icons-material/Event';
import MuiCalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import MuiLocationCityIcon from '@mui/icons-material/LocationCity';
import MuiPhoneIcon from '@mui/icons-material/Phone';
import MuiVideocamIcon from '@mui/icons-material/Videocam';
import { logEvent } from '../../analytics';
import CareInfoDialog from './CareInfoDialog';
import { ErrorSymbol } from '../../jsxElements';
import { openUrlForWebOrMobile } from '../../helpers';
import { CancelStatus, ServiceIcons } from '../../types';
import { formatForAppointmentDisplay } from '../../utils/dateTime';
import { useConfig } from '../../hooks';
import styles from './CareCard.module.css';
import sharedStyles from '../../shared.module.css';

import { AmpFlagSet } from '../../amplitude';

const ServiceIcon: FC<{ serviceIcon: ServiceIcons }> = ({ serviceIcon }) => {
    return (
        <div>
            {serviceIcon?.virtual && (
                <div>
                    <MuiVideocamIcon
                        className={classNames(
                            styles.careCardServiceIcon,
                            styles.careCardIconXLargeFont
                        )}
                    />
                </div>
            )}
            <div className={styles.careCardBreak} />
            {serviceIcon?.office && (
                <div>
                    <MuiLocationCityIcon
                        className={classNames(
                            styles.careCardServiceIcon,
                            styles.careCardIconXLargeFont
                        )}
                    />
                </div>
            )}
            {!serviceIcon?.virtual && !serviceIcon?.office && (
                <div>
                    <MuiCalendarTodayIcon
                        className={classNames(
                            styles.careCardServiceIcon,
                            styles.careCardIconSmallFont
                        )}
                    />
                </div>
            )}
        </div>
    );
};

const Date: FC<{ date: string }> = ({ date }) => {
    const splitDate = formatForAppointmentDisplay(date);
    return (
        <div className={styles.careCardDateWrapper}>
            <p
                className={classNames(
                    styles.careCardDateMonthYear,
                    styles.careCardDate
                )}
            >
                {splitDate[0]}
            </p>
            <p
                className={classNames(
                    styles.careCardDateDay,
                    styles.careCardDate
                )}
            >
                {splitDate[1]}
            </p>
            <p
                className={classNames(
                    styles.careCardDateMonthYear,
                    styles.careCardDate
                )}
            >
                {' '}
                {splitDate[2]}
            </p>
        </div>
    );
};

const CanceledCardMainContainer: FC<{
    cancelStatus: CancelStatus;
    forceHeight: number | undefined;
    subtitle: string;
    title: string;
}> = ({ cancelStatus, forceHeight, subtitle, title }) => {
    return (
        <div
            className={styles.careCardMainContainer}
            style={forceHeight ? { height: forceHeight } : {}}
        >
            <div className={styles.careCardInformationContainer}>
                <div>
                    <p
                        className={classNames(
                            styles.careCardTitle,
                            styles.careCardTitleSpacing
                        )}
                    >
                        {title}
                    </p>
                    <p className={styles.careCardSubtitle}>{subtitle}</p>
                </div>
            </div>
            <p className={styles.careCardStatusFooter}>
                {cancelStatus === 'pending' ? 'CANCELING' : 'CANCELED'}
                {cancelStatus === 'pending' && (
                    <span
                        className={classNames(
                            sharedStyles.ellipsis,
                            styles.careCardEllipsis
                        )}
                    >
                        {'\u2026'}
                    </span>
                )}
            </p>
        </div>
    );
};

const CareCardMainContainer: FC<{
    address?: string;
    cancelStatus?: CancelStatus;
    children: any;
    content: any;
    dialogContent?: any;
    eventIcon?: boolean;
    expandText?: any;
    featureFlags: AmpFlagSet;
    handleToggle: (e: any) => void;
    forceHeight?: number;
    index?: number;
    infoIcon?: boolean;
    locationIcon?: boolean;
    directionsIcon?: boolean;
    noAccordion?: boolean;
    openDialog: boolean;
    phone?: string;
    phoneIcon?: boolean;
    picture?: string;
    recordItemHeight?: (index: number, height: number) => void;
    subtitle?: any;
    title: string;
}> = ({
    address,
    cancelStatus,
    children,
    content,
    dialogContent,
    eventIcon,
    expandText,
    featureFlags,
    handleToggle,
    forceHeight,
    index,
    infoIcon,
    locationIcon,
    directionsIcon,
    noAccordion,
    openDialog,
    phone,
    phoneIcon,
    picture,
    recordItemHeight,
    subtitle,
    title,
}) => {
    const { mychartDomains, tier } = useConfig();
    const divRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (
            divRef &&
            divRef.current &&
            recordItemHeight &&
            typeof index === 'number'
        ) {
            // record height minus padding/margin combination so canceled card
            // will be the same height (no jitter)
            const heightToSet =
                divRef.current.getBoundingClientRect().height - // height of card
                16; // magic number - some combo of padding and margins inside the normal appt card
            // todo: test ^ on different screen sizes
            recordItemHeight(index, heightToSet);
        }
    }, []); // eslint-disable-line

    const careCardContainerClass = classNames(
        noAccordion ? styles.careCardNotExpanded : null,
        styles.careCardMainContainer
    );

    return cancelStatus === 'success' || cancelStatus === 'pending' ? (
        <CanceledCardMainContainer
            cancelStatus={cancelStatus}
            forceHeight={forceHeight}
            subtitle={subtitle}
            title={title}
        />
    ) : (
        <div className={careCardContainerClass} ref={divRef}>
            <div className={styles.careCardInformationContainer}>
                {picture && (
                    <div className={styles.careCardImageWrapper}>
                        <img
                            className={styles.careCardImage}
                            src={picture}
                            alt={'Provider'}
                        />
                    </div>
                )}

                <div>
                    <p className={styles.careCardTitle}>{title}</p>
                    <p className={styles.careCardSubtitle}>{subtitle}</p>
                </div>
            </div>
            {infoIcon ? (
                <InfoIcon
                    className={styles.careCardInfoIcon}
                    onClick={handleToggle}
                />
            ) : (
                <></>
            )}
            <CareInfoDialog
                isOpen={openDialog}
                handleToggle={handleToggle}
                dialogContent={dialogContent}
            />
            <div> {content} </div>
            <div> {children} </div>
            {cancelStatus === 'failure' ? (
                <div className={styles.careCardErrorContainer}>
                    <div className={styles.careCardErrorSymbolContainer}>
                        <ErrorSymbol />
                    </div>
                    <div className={styles.careCardErrorMessage}>
                        I cannot cancel this appointment for you. Please call to
                        cancel this appointment.
                    </div>
                </div>
            ) : null}
            <div className={styles.careCardDetailsWrapper}>
                {expandText && (
                    <div className={styles.careCardExpandMore}>
                        {expandText}
                    </div>
                )}
                <span>
                    {phoneIcon && phone && (
                        <a
                            href={`tel:${phone}`}
                            onClick={(_e) => {
                                logEvent('PC - Prov Card - Click to Call');
                            }}
                        >
                            <MuiPhoneIcon
                                className={classNames(
                                    styles.careCardLocationIcon,
                                    styles.careCardIconMediumFont
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            />
                        </a>
                    )}
                    {locationIcon && address && (
                        <MuiLocationOnIcon
                            className={classNames(
                                styles.careCardLocationIcon,
                                styles.careCardIconMediumFont
                            )}
                            onClick={(e) => {
                                openUrlForWebOrMobile(
                                    address,
                                    featureFlags,
                                    mychartDomains,
                                    tier
                                );
                                e.stopPropagation();
                            }}
                        />
                    )}
                    {directionsIcon && address && (
                        <MuiDirectionsIcon
                            className={classNames(
                                styles.careCardLocationIcon,
                                styles.careCardIconMediumFont
                            )}
                            onClick={(e) => {
                                openUrlForWebOrMobile(
                                    address,
                                    featureFlags,
                                    mychartDomains,
                                    tier
                                );
                                logEvent('PC - Prov Card - Directions Click');
                                e.stopPropagation();
                            }}
                        />
                    )}
                    {eventIcon && (
                        <MuiEventIcon
                            className={classNames(
                                styles.careCardLocationIcon,
                                styles.careCardIconMediumFont
                            )}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        />
                    )}
                </span>
            </div>
        </div>
    );
};

const CareCard: FC<{
    accordion?: boolean;
    address?: string;
    cancelStatus?: CancelStatus;
    children?: React.ReactNode;
    content: any;
    date?: string;
    dialogContent?: any;
    eventIcon?: boolean;
    expanded?: string | false;
    expandText?: any;
    featureFlags: AmpFlagSet;
    forceHeight?: number;
    index?: number;
    infoIcon?: boolean;
    locationIcon?: boolean;
    directionsIcon?: boolean;
    noAccordion?: boolean;
    phone?: string;
    phoneIcon?: boolean;
    picture?: string;
    recordItemHeight?: (index: number, height: number) => void;
    serviceIcon?: ServiceIcons;
    subtitle?: any;
    title: string;
    visitInformation?: string;
}> = ({
    expanded,
    address,
    cancelStatus,
    content,
    date,
    dialogContent,
    eventIcon,
    expandText,
    featureFlags,
    forceHeight,
    index,
    infoIcon,
    locationIcon,
    directionsIcon,
    noAccordion,
    phone,
    phoneIcon,
    picture,
    recordItemHeight,
    serviceIcon,
    subtitle,
    title,
    visitInformation,
    children,
}) => {
    const [openDialog, setOpenDialog] = useState(false);

    const handleToggle: (e: any) => void = () => {
        setOpenDialog(!openDialog);
    };

    const careCardClass = expanded ? styles.careCardAccordion : styles.careCard;
    return (
        <div className={careCardClass}>
            <div className={styles.careCardVisitInformation}>
                {serviceIcon && <ServiceIcon serviceIcon={serviceIcon} />}
                {date && <Date date={date} />}
                {visitInformation && visitInformation}
            </div>
            <CareCardMainContainer
                address={address}
                cancelStatus={cancelStatus}
                children={children}
                content={content}
                dialogContent={dialogContent}
                eventIcon={eventIcon}
                expandText={expandText}
                featureFlags={featureFlags}
                handleToggle={handleToggle}
                forceHeight={forceHeight}
                index={index}
                infoIcon={infoIcon}
                locationIcon={locationIcon}
                directionsIcon={directionsIcon}
                noAccordion={noAccordion}
                openDialog={openDialog}
                phone={phone}
                phoneIcon={phoneIcon}
                picture={picture}
                recordItemHeight={recordItemHeight}
                subtitle={subtitle}
                title={title}
            />
        </div>
    );
};

export default CareCard;
